import React, { useState } from 'react';
import { useZitoneoHotelPresentation } from '../../../contexts/ZitoneoContentContext';
import LoadingPlaceholder from '../../../../../muiComponents/LoadingPlaceholder';
import useTranslate from '../../../../../customHooks/useTranslate';
import TextInput from '../../../../../muiComponents/TextInput/TextInput';
import Button from '../../../../../muiComponents/Button';
import { commonIcons } from '../../../../../utils/commonVariables/commonIcons';
import { useCompetitorSuggestions } from '../../../hooks/useCompetitorSuggestions';
import { useCompetitors } from '../../../hooks/useCompetitors';
import { StoredCompetitor } from '../../../types/competitors';
import { useCompetitorAnalysis } from '../../../hooks/useCompetitorAnalysis';
import CompetitorRow from './CompetitorRow';
import NoAddedServices from './NoAddedServices';
import NoCompetitors from './NoCompetitors';
import { useNotification } from '../../../../../contexts/NotificationsContext';
import { LinearProgress } from '@mui/material';

const CompetitorsNew = () => {
    const { setErrorMessage } = useNotification();
    const { hotel, stats, isLoading } = useZitoneoHotelPresentation();
    const { addedHotelServices } = stats;

    const translate = useTranslate();
    const [freeTextCompetitorName, setFreeTextCompetitorName] = useState('');
    const {
        competitors,
        isLoading: isLoadingCompetitors,
        error: competitorsError,
        saveCompetitors,
        removeCompetitor,
    } = useCompetitors(hotel.id || null);
    const { analyze, isAnalyzing, analyzingCompetitorId } =
        useCompetitorAnalysis({
            hotelId: hotel.idHotel,
            supplierHotelId: Number(hotel.id),
        });
    const { suggestCompetitors, isLoadingAssistant } = useCompetitorSuggestions(
        { competitors, saveCompetitors }
    );

    const handleSuggestCompetitors = async () => {
        const excludedHotels = competitors.map(competitor => ({
            competitorName: competitor.name,
            city: competitor.city,
        }));

        await suggestCompetitors({
            hotelName: hotel.name,
            city: hotel.city,
            country: hotel.country,
            starRating: hotel.category,
            segment: '',
            excludedHotels: JSON.stringify(excludedHotels),
        });
    };

    const handleAnalyzeCompetitor = async (competitor: StoredCompetitor) => {
        await analyze(competitor);
    };

    const handleAnalyzeFreeTextCompetitor = async () => {
        const name = freeTextCompetitorName.trim();
        const isEnqueued = await analyze(name);
        if (isEnqueued) {
            setFreeTextCompetitorName('');
            try {
                await saveCompetitors([
                    {
                        id: crypto.randomUUID(),
                        query: name,
                        name,
                        source: 'manual',
                    },
                ]);
            } catch {
                // useCompetitors already notifies persistence failures.
            }
        }
    };

    const handleDeleteCompetitor = async (competitor: StoredCompetitor) => {
        try {
            await removeCompetitor(competitor.id);
        } catch (error) {
            console.error('Could not delete competitor:', error);
            setErrorMessage('could_not_delete_competitor');
        }
    };

    return (
        <>
            <div
                aria-busy={
                    isLoading ||
                    isLoadingCompetitors ||
                    isLoadingAssistant ||
                    isAnalyzing
                }
            >
                <div className='hotel-presentation__section-controls --center'>
                    <div className='hotel-presentation__competitor-searcher'>
                        <TextInput
                            id='competitor-name'
                            labelText={isLoading ? '' : 'competitor_name'}
                            placeholder={
                                isLoading
                                    ? ''
                                    : translate('competitor_placeholder')
                            }
                            value={freeTextCompetitorName}
                            handleChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                            ) => setFreeTextCompetitorName(event.target.value)}
                            disabled={
                                isLoading || !addedHotelServices || isAnalyzing
                            }
                        />
                    </div>
                    <Button
                        type='button'
                        variant='contained'
                        disabled={
                            isLoading ||
                            isAnalyzing ||
                            isLoadingCompetitors ||
                            isLoadingAssistant ||
                            Boolean(competitorsError) ||
                            !hotel.idHotel ||
                            !freeTextCompetitorName.trim() ||
                            !addedHotelServices
                        }
                        startIcon={commonIcons.ADD}
                        onClick={handleAnalyzeFreeTextCompetitor}
                    >
                        {translate('analyze')}
                    </Button>
                    <Button
                        className='hotel-presentation__suggest-competitor'
                        type='button'
                        variant='outlined'
                        disabled={
                            isLoading ||
                            isLoadingCompetitors ||
                            isLoadingAssistant ||
                            !hotel.id ||
                            !hotel.name.trim() ||
                            !hotel.city.trim() ||
                            !hotel.country.trim() ||
                            !addedHotelServices ||
                            isAnalyzing
                        }
                        onClick={handleSuggestCompetitors}
                        startIcon={commonIcons.AI}
                    >
                        {translate('suggest_competitors')}
                    </Button>
                </div>
                {isLoadingAssistant || isLoading ? (
                    <LoadingPlaceholder
                        variant='rounded'
                        width='100%'
                        height={80}
                        aria-hidden='true'
                    />
                ) : isAnalyzing && !analyzingCompetitorId ? (
                    <div className='flex flex-column gap-20 padding20'>
                        <p>{translate('analyzing_hold')}</p>
                        <LinearProgress
                            aria-label={translate('analyzing_hold')}
                        />
                    </div>
                ) : addedHotelServices ? (
                    competitors.length ? (
                        <ul className='hotel-presentation__competitors-list'>
                            {competitors.map(competitor => (
                                <CompetitorRow
                                    key={competitor.id}
                                    competitor={competitor}
                                    isAnalyzing={
                                        analyzingCompetitorId === competitor.id
                                    }
                                    actionsDisabled={isAnalyzing}
                                    onAnalyze={handleAnalyzeCompetitor}
                                    onDelete={handleDeleteCompetitor}
                                />
                            ))}
                        </ul>
                    ) : (
                        <NoCompetitors />
                    )
                ) : (
                    <NoAddedServices />
                )}
            </div>
        </>
    );
};

export default CompetitorsNew;
